import React, { useState } from 'react';
import client from '../../api/client';
import usePolling from '../../hooks/usePolling';
import MetricCard from '../common/MetricCard';
import MetricGrid from '../common/MetricGrid';
import ChillerControlPanel from '../subsystem/ChillerControlPanel';
import StatusBadge from '../common/StatusBadge';
import TimeRangeSelector from '../common/TimeRangeSelector';
import TimeSeriesChart from '../charts/TimeSeriesChart';
import LoadingSpinner from '../common/LoadingSpinner';
import colors from '../../theme/colors';

function notifyUpdate() {
  window.dispatchEvent(new Event('sv360:data-update'));
}

export default function ChillersPage() {
  const [chiller, setChiller] = useState('chiller1');
  const [range, setRange] = useState('-1h');

  const { data: latest, loading } = usePolling(async () => {
    const res = await client.get(`/api/chillers/latest?chiller=${chiller}`);
    notifyUpdate();
    return res.data.sections;
  }, 5000, [chiller]);

  const { data: tempHistory } = usePolling(async () => {
    const res = await client.get(`/api/chillers/history?range=${range}&chiller=${chiller}&section=sensors&fields=outlet_coolant_temp_c,inlet_coolant_temp_c,ambient_temp_c`);
    return res.data.series;
  }, 10000, [range, chiller]);

  const { data: pressHistory } = usePolling(async () => {
    const res = await client.get(`/api/chillers/history?range=${range}&chiller=${chiller}&section=sensors&fields=inlet_coolant_press_bar,outlet_coolant_press_bar`);
    return res.data.series;
  }, 10000, [range, chiller]);

  const { data: compHistory } = usePolling(async () => {
    const res = await client.get(`/api/chillers/history?range=${range}&chiller=${chiller}&section=components&fields=pump_run_speed_pct,compressor_run_speed_pct,condenser_fan_speed_pct`);
    return res.data.series;
  }, 10000, [range, chiller]);

  if (loading && !latest) return <LoadingSpinner />;

  const control = latest?.control || {};
  const setpoints = latest?.setpoints || {};
  const sensors = latest?.sensors || {};
  const components = latest?.components || {};
  const alarms = latest?.alarms || {};

  const modeLabels = { 0: 'Stop', 1: 'Cycle', 2: 'Cool', 3: 'Heat', 4: 'Auto' };

  // Collect active alarms
  const activeAlarms = Object.entries(alarms)
    .filter(([k, v]) => v === 1 && !k.startsWith('_') && k !== 'section' && k !== 'device' && k !== 'chiller')
    .map(([k]) => k);

  return (
    <div>
      {/* Chiller selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <SelectorBtn label="Chiller 1" active={chiller === 'chiller1'} onClick={() => setChiller('chiller1')} />
        <SelectorBtn label="Chiller 2" active={chiller === 'chiller2'} onClick={() => setChiller('chiller2')} />
      </div>

      {/* Status bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge label={control.system_onoff === 1 ? 'ON' : 'OFF'} color={control.system_onoff === 1 ? 'green' : 'gray'} />
          <StatusBadge label={`Mode: ${modeLabels[control.operating_mode] || '--'}`} color="cyan" />
          <StatusBadge label={`Cool SP: ${setpoints.cool_sp_c ?? '--'}°C`} color="cyan" />
          <StatusBadge label={`Heat SP: ${setpoints.heat_sp_c ?? '--'}°C`} color="cyan" />
          {activeAlarms.length > 0 && <StatusBadge label={`${activeAlarms.length} Alarms`} color="red" />}
        </div>
      </div>

      {/* Sensor metrics */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Sensors</div>
        <MetricGrid minWidth={150}>
          <MetricCard label="Outlet Coolant" value={sensors.outlet_coolant_temp_c} unit="°C" />
          <MetricCard label="Inlet Coolant" value={sensors.inlet_coolant_temp_c} unit="°C" />
          <MetricCard label="Ambient" value={sensors.ambient_temp_c} unit="°C" />
          <MetricCard label="Inlet Pressure" value={sensors.inlet_coolant_press_bar} unit="bar" decimals={2} />
          <MetricCard label="Outlet Pressure" value={sensors.outlet_coolant_press_bar} unit="bar" decimals={2} />
        </MetricGrid>
      </div>

      {/* Component status */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Components</div>
        <MetricGrid minWidth={150}>
          <MetricCard label="Pump Speed" value={components.pump_run_speed_pct} unit="%" decimals={0} />
          <MetricCard label="Pump Gear" value={components.pump_gear} unit="" decimals={0} />
          <MetricCard label="Compressor" value={components.compressor_onoff === 1 ? 'ON' : 'OFF'} decimals={0}
            status={components.compressor_onoff === 1 ? 'normal' : 'offline'} />
          <MetricCard label="Comp Speed" value={components.compressor_run_speed_pct} unit="%" decimals={0} />
          <MetricCard label="Comp Runtime" value={components.compressor_runtime_hr} unit="hr" decimals={0} />
          <MetricCard label="Fan Speed" value={components.condenser_fan_speed_pct} unit="%" decimals={0} />
        </MetricGrid>
      </div>

      {/* Control Panel */}
      <ChillerControlPanel chiller={chiller} />

      {/* Historical charts */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>Temperature History</span>
          <TimeRangeSelector selected={range} onChange={setRange} />
        </div>
        <TimeSeriesChart
          series={tempHistory || {}}
          config={{
            height: 280,
            yAxisLabel: '°C',
            seriesConfig: {
              outlet_coolant_temp_c: { name: 'Outlet', unit: '°C', color: colors.series[0] },
              inlet_coolant_temp_c: { name: 'Inlet', unit: '°C', color: colors.series[1] },
              ambient_temp_c: { name: 'Ambient', unit: '°C', color: colors.series[2] },
            },
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-title">Pressure History</div>
          <TimeSeriesChart
            series={pressHistory || {}}
            config={{
              height: 240,
              yAxisLabel: 'bar',
              seriesConfig: {
                inlet_coolant_press_bar: { name: 'Inlet', unit: 'bar', color: colors.series[0], decimals: 2 },
                outlet_coolant_press_bar: { name: 'Outlet', unit: 'bar', color: colors.series[1], decimals: 2 },
              },
            }}
          />
        </div>
        <div className="card">
          <div className="card-title">Component Speeds</div>
          <TimeSeriesChart
            series={compHistory || {}}
            config={{
              height: 240,
              yAxisLabel: '%',
              seriesConfig: {
                pump_run_speed_pct: { name: 'Pump', unit: '%', color: colors.series[0], decimals: 0 },
                compressor_run_speed_pct: { name: 'Compressor', unit: '%', color: colors.series[1], decimals: 0 },
                condenser_fan_speed_pct: { name: 'Fan', unit: '%', color: colors.series[2], decimals: 0 },
              },
            }}
          />
        </div>
      </div>

      {/* Alarm table */}
      {activeAlarms.length > 0 && (
        <div className="card">
          <div className="card-title">Active Alarms</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {activeAlarms.map(a => (
              <StatusBadge key={a} label={a.replace(/_/g, ' ')} color="red" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SelectorBtn({ label, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '6px 16px',
      borderRadius: 6,
      border: 'none',
      fontSize: 12,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? colors.accent : colors.surface,
      color: active ? colors.bg : colors.textMuted,
      opacity: disabled ? 0.4 : 1,
    }}>
      {label}
    </button>
  );
}
